/**
 * Inbound Shipments API Client
 *
 * One function per phase of the inbound truck workflow.
 * Used by the inbound-simulator section in admin portal AND can be wired
 * into sagas later for production flows.
 */

import { getHttpClient } from '@spcc/core'
import type { InboundShipmentStats } from '../../domain/types'

const PURCHASE_ORDER_QR_TYPE = 'purchase_order'

interface StrapiResponse<T> {
  data: T
  meta?: unknown
}

export interface PurchaseOrderLineDto {
  id: number
  sapItemNumber?: string | null
  orderedQuantity: number
  uom: string
  material?: {
    id: number
    code?: string | null
    sapMaterialCode?: string | null
    name?: string | null
  } | null
}

export interface StorageLocationDto {
  id: number
  code: string
  sapStorageLocationCode?: string | null
  isActive?: boolean
}

export interface InboundShipmentDto {
  id: number
  documentId?: string
  shipmentNumber: string
  status:
    | 'arrived'
    | 'first_weight_done'
    | 'unloaded'
    | 'second_weight_done'
    | 'grn_posted'
    | 'grn_skipped'
  vehiclePlate?: string | null
  driverName?: string | null
  arrivedAt: string
  departedAt?: string | null
  sapSyncStatus?: string | null
  sapSyncError?: string | null
  sapFirstWeightSyncedAt?: string | null
  sapSecondWeightSyncedAt?: string | null
  sapGrnSyncedAt?: string | null
  purchaseOrder?: {
    id: number
    poNumber?: string
    sapPurchaseOrderNumber?: string | null
    purchaseOrderLines?: PurchaseOrderLineDto[]
  }
  purchaseOrderLine?: PurchaseOrderLineDto | null
  factory?: {
    id: number
    code?: string
    sapPlantCode?: string | null
    storageLocations?: StorageLocationDto[]
  } | null
  storageLocation?: StorageLocationDto | null
  goodsReceipt?: { id: number; grnNumber?: string; sapMaterialDocument?: string | null }
  weighbridgeTicket?: {
    id: number
    ticketNumber?: string | null
    grossWeight?: number | null
    tareWeight?: number | null
    netWeight?: number | null
  } | null
}

export interface InboundShipmentTimelineEventDto {
  id: number
  documentId?: string
  entityType?: 'INBOUND_SHIPMENT' | string
  entityId?: string
  action: string
  description?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  metadata?: {
    shipmentNumber?: string | null
    purchaseOrderNumber?: string | null
    sapPurchaseOrderNumber?: string | null
    purchaseOrderLineItemNumber?: string | null
    vehiclePlate?: string | null
    status?: string | null
    step?: string | null
    success?: boolean | null
    weightKg?: number | null
    grossWeightKg?: number | null
    netWeightKg?: number | null
    weightStage?: string | null
    direction?: string | null
    grnId?: number | null
    error?: string | null
    [key: string]: unknown
  } | null
  user?: {
    id: number
    username?: string | null
    email?: string | null
  } | null
}

export interface QrScanResponse {
  success: boolean
  data: {
    shipmentId?: number
    shipmentNumber?: string
    shipmentStatus?: string
    purchaseOrderNumber?: string
  }
  meta: {
    direction: string
    action: string
    subjectType: string
  }
}

export interface CameraReadingResponse {
  success?: boolean
  data?: Partial<InboundShipmentDto> | Record<string, unknown> | null
  command?: unknown
  meta?: {
    action?: string
    direction?: string
    shipmentNumber?: string | null
    pendingCommandId?: number | null
    [key: string]: unknown
  }
}

// ============================================================================
// Step 1 — Simulate ANPR plate read at gate
// ============================================================================
export async function simulateAnprPlateRead(input: {
  siteId: string
  zoneCode: string
  cameraIp?: string
  licensePlate: string
}): Promise<CameraReadingResponse> {
  const http = getHttpClient()
  const response = await http.post<CameraReadingResponse>('/api/cameras/reading', {
    siteId: input.siteId,
    zoneCode: input.zoneCode,
    cameraIp: input.cameraIp ?? '127.0.0.1',
    licensePlate: input.licensePlate,
    confidence: 0.99,
    dateTime: new Date().toISOString(),
  })
  return response.data
}

// ============================================================================
// Step 2 — Driver scans PO QR (creates shipment + pairs plate)
// ============================================================================
export async function scanPurchaseOrderQr(input: {
  siteId: string
  zoneCode: string
  pathId: string
  purchaseOrderNumber: string
  purchaseOrderLineItemNumber: string
}): Promise<QrScanResponse> {
  const http = getHttpClient()
  const response = await http.post<QrScanResponse>('/api/qr-scans/reading', {
    type: PURCHASE_ORDER_QR_TYPE,
    siteId: input.siteId,
    zoneCode: input.zoneCode,
    pathId: input.pathId,
    purchaseOrderNumber: input.purchaseOrderNumber,
    purchaseOrderLineItemNumber: input.purchaseOrderLineItemNumber,
    dateTime: new Date().toISOString(),
  })
  return response.data
}

export async function simulateInboundWeighbridgeQrScan(input: {
  siteId: string
  zoneCode: string
  pathId: string
  purchaseOrderNumber: string
  purchaseOrderLineItemNumber: string
}): Promise<QrScanResponse> {
  const http = getHttpClient()
  const response = await http.post<QrScanResponse>('/api/qr-scans/reading', {
    type: PURCHASE_ORDER_QR_TYPE,
    siteId: input.siteId,
    zoneCode: input.zoneCode,
    pathId: input.pathId,
    purchaseOrderNumber: input.purchaseOrderNumber,
    purchaseOrderLineItemNumber: input.purchaseOrderLineItemNumber,
    dateTime: new Date().toISOString(),
  })
  return response.data
}

// ============================================================================
// Step 3 — First weight (gross / loaded)
// ============================================================================
export async function recordFirstWeight(input: {
  shipmentNumber: string
  weight: number
}): Promise<StrapiResponse<InboundShipmentDto>> {
  const http = getHttpClient()
  const shipmentNumber = encodeURIComponent(input.shipmentNumber)
  const response = await http.post<StrapiResponse<InboundShipmentDto>>(
    `/api/inbound-shipments/${shipmentNumber}/weight`,
    {
      weightType: 'F',
      weight: input.weight,
    },
  )
  return response.data
}

// ============================================================================
// Step 4 — Second weight (tare / empty after unload)
// Backend updates the existing weighbridge ticket with tare + net, then auto-creates GRN.
// No new ticket is created on the frontend side.
// ============================================================================
export async function recordSecondWeight(input: {
  shipmentNumber: string
  weight: number
}): Promise<StrapiResponse<InboundShipmentDto>> {
  const http = getHttpClient()
  const shipmentNumber = encodeURIComponent(input.shipmentNumber)
  const response = await http.post<StrapiResponse<InboundShipmentDto>>(
    `/api/inbound-shipments/${shipmentNumber}/weight`,
    {
      weightType: 'S',
      weight: input.weight,
    },
  )
  return response.data
}

// ============================================================================
// Step 5 — Exit scan (gate refuses if GRN not posted)
// ============================================================================
export async function simulateExitScan(input: {
  siteId: string
  zoneCode: string
  pathId: string
  purchaseOrderNumber: string
  purchaseOrderLineItemNumber: string
}): Promise<QrScanResponse> {
  const http = getHttpClient()
  const response = await http.post<QrScanResponse>('/api/qr-scans/reading', {
    type: PURCHASE_ORDER_QR_TYPE,
    siteId: input.siteId,
    zoneCode: input.zoneCode,
    pathId: input.pathId,
    purchaseOrderNumber: input.purchaseOrderNumber,
    purchaseOrderLineItemNumber: input.purchaseOrderLineItemNumber,
    dateTime: new Date().toISOString(),
  })
  return response.data
}

// ============================================================================
// Gate-context lookups (replaces free-text simulator inputs)
// ============================================================================

export interface GateContextOption {
  id: number
  documentId: string
  code: string
  name: string
  direction: 'entry' | 'exit' | 'bidirectional'
  factoryCode: string | null
  factoryName: string | null
  zoneCode: string | null
  zoneName: string | null
  zoneType: string | null
}

export interface GateRuntimeContext {
  gate: GateContextOption
  pathId: string | null
  cameraIp: string | null
  qrReaderCode: string | null
  cameraCode: string | null
}

interface GateDto {
  id: number
  documentId: string
  code: string
  name: string
  direction: 'entry' | 'exit' | 'bidirectional'
  factory?: { id: number; code?: string; name?: string } | null
  zone?: { id: number; code?: string; name?: string; type?: string } | null
}

interface QrReaderDto {
  id: number
  pathId: string
  code: string
}

interface CameraDto {
  id: number
  ipAddress: string | null
  code: string
}

/** List active gates for the simulator dropdown. */
export async function fetchGatesForSimulator(): Promise<GateContextOption[]> {
  const http = getHttpClient()
  const params = new URLSearchParams({
    'pagination[pageSize]': '200',
    'filters[gateStatus][$eq]': 'active',
    'sort[0]': 'code:asc',
    'populate[factory][fields][0]': 'id',
    'populate[factory][fields][1]': 'code',
    'populate[factory][fields][2]': 'name',
    'populate[zone][fields][0]': 'id',
    'populate[zone][fields][1]': 'code',
    'populate[zone][fields][2]': 'name',
    'populate[zone][fields][3]': 'type',
  })
  const response = await http.get<{ data: GateDto[] }>(
    `/api/gates?${params.toString()}`,
  )
  return response.data.data.map((g) => ({
    id: g.id,
    documentId: g.documentId,
    code: g.code,
    name: g.name,
    direction: g.direction,
    factoryCode: g.factory?.code ?? null,
    factoryName: g.factory?.name ?? null,
    zoneCode: g.zone?.code ?? null,
    zoneName: g.zone?.name ?? null,
    zoneType: g.zone?.type ?? null,
  }))
}

/** Resolve qr-reader (pathId) + camera (ipAddress) attached to a gate. */
export async function fetchGateRuntimeContext(
  gate: GateContextOption,
): Promise<GateRuntimeContext> {
  const http = getHttpClient()

  const qrParams = new URLSearchParams({
    'pagination[pageSize]': '5',
    'filters[gate][id][$eq]': String(gate.id),
    'filters[readerStatus][$eq]': 'active',
    'sort[0]': 'createdAt:desc',
  })
  const qrResp = await http.get<{ data: QrReaderDto[] }>(
    `/api/qr-readers?${qrParams.toString()}`,
  )
  const reader = qrResp.data.data[0] ?? null

  const camParams = new URLSearchParams({
    'pagination[pageSize]': '5',
    'filters[gate][id][$eq]': String(gate.id),
    'sort[0]': 'createdAt:desc',
  })
  const camResp = await http.get<{ data: CameraDto[] }>(
    `/api/cameras?${camParams.toString()}`,
  )
  const camera = camResp.data.data[0] ?? null

  return {
    gate,
    pathId: reader?.pathId ?? null,
    cameraIp: camera?.ipAddress ?? null,
    qrReaderCode: reader?.code ?? null,
    cameraCode: camera?.code ?? null,
  }
}

// ============================================================================
// Read-only helpers
// ============================================================================

export interface InboundShipmentsListParams {
  page?: number
  pageSize?: number
  /** A single status, or a list to match any of them ($in). */
  status?: string | string[]
  search?: string
  sapSyncStatus?: string
  grnStatus?: string
  weightStatus?: string
  materialId?: string
  supplierId?: string
}

export interface InboundShipmentsListResponse {
  data: InboundShipmentDto[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
    statusBreakdown?: InboundShipmentStats
  }
}

function applyInboundShipmentFilters(
  qs: URLSearchParams,
  params: Omit<InboundShipmentsListParams, 'page' | 'pageSize'>,
): void {
  const { status, search, sapSyncStatus, grnStatus, weightStatus, materialId, supplierId } = params

  if (Array.isArray(status)) {
    status.forEach((value, index) => qs.set(`filters[status][$in][${index}]`, value))
  } else if (status && status !== 'all') {
    qs.set('filters[status][$eq]', status)
  }

  if (search) {
    qs.set('filters[$or][0][shipmentNumber][$containsi]', search)
    qs.set('filters[$or][1][vehiclePlate][$containsi]', search)
    qs.set('filters[$or][2][purchaseOrder][poNumber][$containsi]', search)
  }

  if (sapSyncStatus && sapSyncStatus !== 'all') {
    qs.set('filters[sapSyncStatus][$eq]', sapSyncStatus)
  }

  if (grnStatus === 'posted') {
    qs.set('filters[goodsReceipt][id][$notNull]', 'true')
  }

  if (grnStatus === 'not_posted') {
    qs.set('filters[goodsReceipt][id][$null]', 'true')
  }

  if (weightStatus === 'gross_recorded') {
    qs.set('filters[weighbridgeTicket][grossWeight][$notNull]', 'true')
  }

  if (weightStatus === 'net_recorded') {
    qs.set('filters[weighbridgeTicket][netWeight][$notNull]', 'true')
  }

  if (weightStatus === 'missing_weight') {
    qs.set('filters[weighbridgeTicket][netWeight][$null]', 'true')
  }

  if (materialId && materialId !== 'all') {
    qs.set('filters[purchaseOrderLine][material][id][$eq]', materialId)
  }

  if (supplierId && supplierId !== 'all') {
    qs.set('filters[purchaseOrder][supplier][id][$eq]', supplierId)
  }
}

export async function fetchInboundShipments(
  params: InboundShipmentsListParams = {},
): Promise<InboundShipmentsListResponse> {
  const http = getHttpClient()
  const { page = 1, pageSize = 25, status, search, sapSyncStatus, grnStatus, weightStatus, materialId, supplierId } = params
  const qs = new URLSearchParams({
    'pagination[page]': String(page),
    'pagination[pageSize]': String(pageSize),
    'sort[0]': 'arrivedAt:desc',
    'populate[purchaseOrder][fields][0]': 'id',
    'populate[purchaseOrder][fields][1]': 'poNumber',
    'populate[purchaseOrder][fields][2]': 'sapPurchaseOrderNumber',
    'populate[purchaseOrderLine][fields][0]': 'id',
    'populate[purchaseOrderLine][fields][1]': 'sapItemNumber',
    'populate[purchaseOrderLine][fields][2]': 'orderedQuantity',
    'populate[purchaseOrderLine][fields][3]': 'uom',
    'populate[purchaseOrderLine][populate][material][fields][0]': 'id',
    'populate[purchaseOrderLine][populate][material][fields][1]': 'code',
    'populate[purchaseOrderLine][populate][material][fields][2]': 'name',
    'populate[factory][fields][0]': 'id',
    'populate[factory][fields][1]': 'code',
    'populate[factory][fields][2]': 'sapPlantCode',
    'populate[storageLocation][fields][0]': 'id',
    'populate[storageLocation][fields][1]': 'code',
    'populate[storageLocation][fields][2]': 'sapStorageLocationCode',
    'populate[goodsReceipt][fields][0]': 'id',
    'populate[goodsReceipt][fields][1]': 'grnNumber',
    'populate[goodsReceipt][fields][2]': 'sapMaterialDocument',
    'populate[weighbridgeTicket][fields][0]': 'id',
    'populate[weighbridgeTicket][fields][1]': 'ticketNumber',
    'populate[weighbridgeTicket][fields][2]': 'grossWeight',
    'populate[weighbridgeTicket][fields][3]': 'tareWeight',
    'populate[weighbridgeTicket][fields][4]': 'netWeight',
  })
  applyInboundShipmentFilters(qs, { status, search, sapSyncStatus, grnStatus, weightStatus, materialId, supplierId })

  const response = await http.get<InboundShipmentsListResponse>(
    `/api/inbound-shipments?${qs.toString()}`,
  )
  return response.data
}

export interface InboundShipmentsExportRequest
  extends Omit<InboundShipmentsListParams, 'page' | 'pageSize'> {
  email: string
}

export type InboundShipmentsExportResult =
  | { mode: 'download'; count: number; fileName: string; shipments: InboundShipmentDto[] }
  | { mode: 'email'; count: number; fileName: string; email: string }

/**
 * Export all inbound shipments matching current filters (no pagination limit).
 * Same email-aware threshold pattern as ordersApi.exportAll: small result sets
 * download immediately, large ones are emailed by the backend.
 */
export async function exportInboundShipments(
  request: InboundShipmentsExportRequest,
): Promise<InboundShipmentsExportResult> {
  const http = getHttpClient()
  const { email, ...filters } = request
  const response = await http.post<StrapiResponse<Record<string, unknown>>>(
    '/api/inbound-shipments/export',
    { email, ...filters },
  )
  const payload = response.data?.data as Record<string, unknown> | undefined

  if (payload?.mode === 'email') {
    return {
      mode: 'email',
      count: typeof payload.count === 'number' ? payload.count : 0,
      fileName: typeof payload.fileName === 'string' ? payload.fileName : 'inbound-shipments',
      email: typeof payload.email === 'string' ? payload.email : email,
    }
  }

  const rawShipments = Array.isArray(payload?.data) ? (payload.data as InboundShipmentDto[]) : []
  return {
    mode: 'download',
    count: typeof payload?.count === 'number' ? payload.count : rawShipments.length,
    fileName: typeof payload?.fileName === 'string' ? payload.fileName : 'inbound-shipments',
    shipments: rawShipments,
  }
}

export async function fetchInboundShipmentStats(
  params: Omit<InboundShipmentsListParams, 'page' | 'pageSize' | 'status'> = {},
): Promise<InboundShipmentStats> {
  const response = await fetchInboundShipments({
    ...params,
    page: 1,
    pageSize: 1,
  })
  return response.meta.statusBreakdown ?? {
    arrived: 0,
    first_weight_done: 0,
    unloaded: 0,
    second_weight_done: 0,
    grn_posted: 0,
    grn_skipped: 0,
    total: response.meta.pagination.total,
  }
}

export async function fetchInboundShipment(
  id: number,
): Promise<StrapiResponse<InboundShipmentDto>> {
  const http = getHttpClient()
  // Strapi v5 findOne (/api/inbound-shipments/:documentId) expects a documentId string.
  // We only have the numeric id from the QR scan, so use a filtered list query instead.
  const params = new URLSearchParams({
    'filters[id][$eq]': String(id),
    'pagination[pageSize]': '1',
    'populate[purchaseOrder][fields][0]': 'id',
    'populate[purchaseOrder][fields][1]': 'poNumber',
    'populate[purchaseOrder][fields][2]': 'sapPurchaseOrderNumber',
    'populate[purchaseOrderLine][fields][0]': 'id',
    'populate[purchaseOrderLine][fields][1]': 'sapItemNumber',
    'populate[purchaseOrderLine][fields][2]': 'orderedQuantity',
    'populate[purchaseOrderLine][fields][3]': 'uom',
    'populate[purchaseOrderLine][populate][material][fields][0]': 'id',
    'populate[purchaseOrderLine][populate][material][fields][1]': 'code',
    'populate[purchaseOrderLine][populate][material][fields][2]': 'sapMaterialCode',
    'populate[purchaseOrderLine][populate][material][fields][3]': 'name',
    'populate[purchaseOrder][populate][purchaseOrderLines][fields][0]': 'id',
    'populate[purchaseOrder][populate][purchaseOrderLines][fields][1]': 'sapItemNumber',
    'populate[purchaseOrder][populate][purchaseOrderLines][fields][2]': 'orderedQuantity',
    'populate[purchaseOrder][populate][purchaseOrderLines][fields][3]': 'uom',
    'populate[purchaseOrder][populate][purchaseOrderLines][populate][material][fields][0]': 'id',
    'populate[purchaseOrder][populate][purchaseOrderLines][populate][material][fields][1]': 'code',
    'populate[purchaseOrder][populate][purchaseOrderLines][populate][material][fields][2]': 'sapMaterialCode',
    'populate[purchaseOrder][populate][purchaseOrderLines][populate][material][fields][3]': 'name',
    'populate[factory][fields][0]': 'id',
    'populate[factory][fields][1]': 'code',
    'populate[factory][fields][2]': 'sapPlantCode',
    'populate[factory][populate][storageLocations][fields][0]': 'id',
    'populate[factory][populate][storageLocations][fields][1]': 'code',
    'populate[factory][populate][storageLocations][fields][2]': 'sapStorageLocationCode',
    'populate[factory][populate][storageLocations][fields][3]': 'isActive',
    'populate[storageLocation][fields][0]': 'id',
    'populate[storageLocation][fields][1]': 'code',
    'populate[storageLocation][fields][2]': 'sapStorageLocationCode',
    'populate[goodsReceipt][fields][0]': 'id',
    'populate[goodsReceipt][fields][1]': 'grnNumber',
    'populate[goodsReceipt][fields][2]': 'sapMaterialDocument',
    'populate[weighbridgeTicket][fields][0]': 'id',
    'populate[weighbridgeTicket][fields][1]': 'ticketNumber',
    'populate[weighbridgeTicket][fields][2]': 'grossWeight',
    'populate[weighbridgeTicket][fields][3]': 'tareWeight',
    'populate[weighbridgeTicket][fields][4]': 'netWeight',
  })
  const response = await http.get<{ data: InboundShipmentDto[]; meta: unknown }>(
    `/api/inbound-shipments?${params.toString()}`,
  )
  const item = response.data.data[0]
  if (!item) {
    throw new Error(`Inbound shipment with id ${id} not found`)
  }
  return { data: item }
}

export async function fetchInboundShipmentByNumber(
  shipmentNumber: string,
): Promise<StrapiResponse<InboundShipmentDto>> {
  const http = getHttpClient()
  const encodedShipmentNumber = encodeURIComponent(shipmentNumber.trim())
  const response = await http.get<StrapiResponse<InboundShipmentDto>>(
    `/api/inbound-shipments/${encodedShipmentNumber}/details`,
  )
  return response.data
}

export interface UpdateInboundShipmentRequest {
  vehiclePlate?: string | null
  driverName?: string | null
}

export async function updateInboundShipment(
  documentId: string,
  data: UpdateInboundShipmentRequest,
): Promise<StrapiResponse<InboundShipmentDto>> {
  const http = getHttpClient()
  const response = await http.put<StrapiResponse<InboundShipmentDto>>(
    `/api/inbound-shipments/${encodeURIComponent(documentId)}`,
    { data },
  )
  return response.data
}

export async function fetchInboundShipmentTimeline(
  shipmentNumber: string,
): Promise<StrapiResponse<InboundShipmentTimelineEventDto[]>> {
  const http = getHttpClient()
  const encodedShipmentNumber = encodeURIComponent(shipmentNumber.trim())
  const response = await http.get<StrapiResponse<InboundShipmentTimelineEventDto[]>>(
    `/api/inbound-shipments/${encodedShipmentNumber}/timeline`,
  )
  return response.data
}

export async function fetchPurchaseOrderQrData(id: number): Promise<{
  purchaseOrder: { id: number; documentId: string; poNumber: string }
  qrPayload: Record<string, unknown>
  qrText: string
}> {
  const http = getHttpClient()
  const response = await http.get<{
    data: {
      purchaseOrder: { id: number; documentId: string; poNumber: string }
      qrPayload: Record<string, unknown>
      qrText: string
    }
  }>(`/api/purchase-orders/${id}/qr-data`)
  return response.data.data
}

// ============================================================================
// Kiosk fallback — driver registers his own arrival
// ============================================================================

export interface KioskPoLineOption {
  id: number
  sapItemNumber: string
  materialName: string
  orderedQuantity: number
  remainingQuantity: number
  uom: string
}

/**
 * Open lines on a PO, for the kiosk picker. Uses the plain list endpoint, which already
 * enriches each line with `remainingQuantity` — a driver recognizes "60 of 100 ton left"
 * far better than a SAP item number.
 */
export async function fetchPurchaseOrderLinesByPoNumber(
  poNumber: string,
): Promise<KioskPoLineOption[]> {
  const http = getHttpClient()
  const response = await http.get<StrapiResponse<Array<Record<string, any>>>>(
    '/api/purchase-order-lines',
    {
      params: {
        'filters[purchaseOrder][poNumber][$eq]': poNumber.trim(),
        'populate[material][fields][0]': 'name',
        'pagination[pageSize]': 100,
      },
    },
  )

  const rows = Array.isArray(response.data?.data) ? response.data.data : []
  return rows
    .filter((row) => Boolean(row?.sapItemNumber))
    .map((row) => ({
      id: row.id,
      sapItemNumber: String(row.sapItemNumber),
      materialName: row.material?.name ?? row.sapItemText ?? String(row.sapItemNumber),
      orderedQuantity: Number(row.orderedQuantity ?? 0),
      remainingQuantity: Number(row.remainingQuantity ?? row.orderedQuantity ?? 0),
      uom: row.uom ?? 'ton',
    }))
}

export interface KioskArrivalResult {
  shipment: {
    id: number
    shipmentNumber: string
    status: string
    vehiclePlate?: string | null
  }
  qrPayload: Record<string, unknown>
  qrText: string
}

/** Creates the inbound shipment from kiosk-entered details. Never opens a gate. */
export async function registerKioskArrival(input: {
  poNumber: string
  lineItemNumber: string
  licensePlate: string
  driverName?: string
  driverPhone?: string
}): Promise<KioskArrivalResult> {
  const http = getHttpClient()
  const response = await http.post<StrapiResponse<KioskArrivalResult>>(
    '/api/inbound-shipments/kiosk-arrival',
    input,
  )
  return response.data.data
}

export const inboundShipmentsApi = {
  simulateAnprPlateRead,
  fetchPurchaseOrderLinesByPoNumber,
  registerKioskArrival,
  scanPurchaseOrderQr,
  simulateInboundWeighbridgeQrScan,
  recordFirstWeight,
  recordSecondWeight,
  simulateExitScan,
  fetchInboundShipment,
  fetchInboundShipmentByNumber,
  updateInboundShipment,
  fetchInboundShipmentTimeline,
  fetchInboundShipments,
  fetchInboundShipmentStats,
  fetchPurchaseOrderQrData,
  fetchGatesForSimulator,
  fetchGateRuntimeContext,
}
